import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  // ── Independent comparison state ──
  const [compMonth, setCompMonth] = useState(new Date().getMonth() + 1);
  const [compYear, setCompYear] = useState(new Date().getFullYear());
  const [compData, setCompData] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compExpanded, setCompExpanded] = useState(false);
  const [compError, setCompError] = useState(false);

  useEffect(() => { if (!companyId) return; fetchDashboard(); }, [companyId, month, year]);
  useEffect(() => { if (!companyId) return; fetchComparison(compMonth, compYear); }, [companyId, compMonth, compYear]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/outsourcing/overview?month=${month}&year=${year}`, { headers: getAuthHeaders() });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchComparison = async (m, y) => {
    setCompLoading(true);
    setCompError(false);
    try {
      const res = await fetch(`${API}/companies/${companyId}/outsourcing/overview/comparison?month=${m}&year=${y}`, { headers: getAuthHeaders() });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      if (!res.ok) { setCompError(true); setCompLoading(false); return; }
      setCompData(await res.json());
    } catch (e) { console.error(e); setCompError(true); }
    setCompLoading(false);
  };

  const goCompPrev = () => {
    let m = compMonth - 1;
    let y = compYear;
    if (m < 1) { m = 12; y--; }
    setCompMonth(m);
    setCompYear(y);
  };

  const goCompNext = () => {
    let m = compMonth + 1;
    let y = compYear;
    if (m > 12) { m = 1; y++; }
    setCompMonth(m);
    setCompYear(y);
  };

  const monthName = (m) => new Date(2000, m - 1).toLocaleString('default', { month: 'long' });

  if (!companyId) return <div style={styles.page}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={styles.page}><p>Loading dashboard...</p></div>;
  if (!data) return <div style={styles.page}><p>Failed to load dashboard.</p></div>;

  const severityColor = (s) => s === 'error' ? '#e74c3c' : s === 'warning' ? '#f39c12' : '#3498db';

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Outsourcing Overview</h2>

      {/* Month Selector */}
      <div style={styles.filterRow}>
        <select value={month} onChange={e => setMonth(+e.target.value)} style={styles.select}>
          {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('default',{month:'long'})}</option>)}
        </select>
        <select value={year} onChange={e => setYear(+e.target.value)} style={styles.select}>
          {[2024,2025,2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      <div style={styles.cardsGrid}>
        {(data.summary_cards || []).map((card, i) => (
          <div key={i} style={styles.card}>
            <div style={styles.cardLabel}>{card.label}</div>
            <div style={styles.cardValue}>
              {card.format === 'currency' ? `$${Number(card.value).toLocaleString('en-US',{minimumFractionDigits:2})}` : card.value}
            </div>
          </div>
        ))}
      </div>

      {/* ══════════ MONTH-TO-MONTH COMPARISON (navigable + expandable) ══════════ */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Month-to-Month Comparison</h3>

        {/* Navigation row: ◀ month label ▶ */}
        <div style={styles.navRow}>
          <button onClick={goCompPrev} style={styles.navBtn} title="Previous month">◀</button>
          <span style={styles.navLabel}>{monthName(compMonth)} {compYear}</span>
          <button onClick={goCompNext} style={styles.navBtn} title="Next month">▶</button>
        </div>

        {compLoading && <p style={{ textAlign: 'center', color: '#999' }}>Loading comparison...</p>}
        {compError && !compLoading && <p style={{ textAlign: 'center', color: '#e74c3c' }}>Failed to load comparison data.</p>}

        {compData && !compLoading && !compError && (
          <>
            {/* Overall comparison */}
            <div style={styles.comparisonRow}>
              <div style={styles.compCol}>
                <div style={styles.compLabel}>{compData.previous_month}</div>
                <div style={styles.compValue}>${Number(compData.previous_total).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
                <div style={styles.compSub}>{compData.previous_employees} employees</div>
              </div>
              <div style={styles.compArrow}>
                <span style={{fontSize:24, color: compData.change_percentage >= 0 ? '#27ae60' : '#e74c3c'}}>
                  {compData.change_percentage >= 0 ? '▲' : '▼'} {Math.abs(compData.change_percentage)}%
                </span>
              </div>
              <div style={styles.compCol}>
                <div style={styles.compLabel}>{compData.current_month}</div>
                <div style={styles.compValue}>${Number(compData.current_total).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
                <div style={styles.compSub}>{compData.current_employees} employees</div>
              </div>
            </div>

            {/* Expand / Collapse toggle */}
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button
                onClick={() => setCompExpanded(!compExpanded)}
                style={styles.expandBtn}
              >
                {compExpanded ? '▲ Hide Client Details' : '▼ Show Client Details'}
              </button>
            </div>

            {/* Per-client breakdown table */}
            {compExpanded && (compData.clients || []).length > 0 && (
              <div style={{ ...styles.tableWrap, marginTop: 14 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Client</th>
                      <th style={styles.th}>Code</th>
                      <th style={styles.th}>Prev Total</th>
                      <th style={styles.th}>Curr Total</th>
                      <th style={styles.th}>Change</th>
                      <th style={styles.th}>%</th>
                      <th style={styles.th}>Prev Emp</th>
                      <th style={styles.th}>Curr Emp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compData.clients.map((c, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={styles.td}>{c.client_name}</td>
                        <td style={styles.td}>{c.client_code}</td>
                        <td style={styles.td}>${Number(c.previous_total).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                        <td style={styles.td}>${Number(c.current_total).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                        <td style={{...styles.td, color: c.change >= 0 ? '#27ae60' : '#e74c3c', fontWeight: 600}}>
                          {c.change >= 0 ? '+' : ''}{Number(c.change).toLocaleString('en-US',{minimumFractionDigits:2})}
                        </td>
                        <td style={{...styles.td, color: c.change_pct >= 0 ? '#27ae60' : '#e74c3c', fontWeight: 600}}>
                          {c.change_pct >= 0 ? '+' : ''}{c.change_pct}%
                        </td>
                        <td style={styles.td}>{c.previous_employees}</td>
                        <td style={styles.td}>{c.current_employees}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {compExpanded && (compData.clients || []).length === 0 && (
              <p style={{ textAlign: 'center', color: '#999', marginTop: 10 }}>No client data for this period.</p>
            )}
          </>
        )}
      </div>

      {/* Client Billing Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Client Billing Summary — {month}/{year}</h3>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Client</th>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Employees</th>
                <th style={styles.th}>Billing Status</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Accounting</th>
              </tr>
            </thead>
            <tbody>
              {(data.client_billing_summary || []).map((c, i) => (
                <tr key={i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td style={styles.td}>{c.client_name}</td>
                  <td style={styles.td}>{c.client_code}</td>
                  <td style={styles.td}>{c.active_employees}</td>
                  <td style={styles.td}><span style={{...styles.badge, backgroundColor: statusColor(c.billing_status)}}>{c.billing_status}</span></td>
                  <td style={styles.td}>${Number(c.total_billable).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td style={styles.td}><span style={{...styles.badge, backgroundColor: acctColor(c.accounting_status)}}>{c.accounting_status}</span></td>
                </tr>
              ))}
              {(data.client_billing_summary || []).length === 0 && (
                <tr><td colSpan={6} style={{...styles.td, textAlign:'center'}}>No active clients</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Actions */}
      {(data.pending_actions || []).length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Pending Actions</h3>
          {data.pending_actions.map((a, i) => (
            <div key={i} style={{...styles.actionItem, borderLeft: `4px solid ${severityColor(a.severity)}`}}>
              <span style={{fontWeight:600, color: severityColor(a.severity)}}>{a.type.replace(/_/g,' ').toUpperCase()}</span>
              <span style={{marginLeft:12}}>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Recent Activity</h3>
        {(data.recent_activity || []).length === 0 && <p style={{color:'#999'}}>No recent activity.</p>}
        {(data.recent_activity || []).slice(0, 10).map((log, i) => (
          <div key={i} style={styles.activityItem}>
            <div style={styles.activityMeta}>
              <span style={{fontWeight:600}}>{log.user?.name || 'System'}</span>
              <span style={{color:'#999',marginLeft:8,fontSize:13}}>{new Date(log.created_at).toLocaleString()}</span>
            </div>
            <div style={{fontSize:14}}>{log.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function statusColor(s) {
  const map = { draft:'#95a5a6', reviewed:'#3498db', approved:'#27ae60', invoiced:'#8e44ad', paid:'#2ecc71', not_generated:'#bdc3c7' };
  return map[s] || '#95a5a6';
}
function acctColor(s) {
  const map = { not_sent:'#95a5a6', sent:'#3498db', invoice_created:'#2980b9', invoiced:'#8e44ad', paid:'#27ae60', 'n/a':'#bdc3c7' };
  return map[s] || '#95a5a6';
}

const styles = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 16 },
  filterRow: { display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' },
  select: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14 },
  cardsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16, marginBottom:24 },
  card: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', textAlign:'center' },
  cardLabel: { fontSize:13, color:'#888', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 },
  cardValue: { fontSize:26, fontWeight:700, color:'#2c3e50' },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:20 },
  sectionTitle: { fontSize:18, fontWeight:600, marginBottom:14, borderBottom:'2px solid #3498db', paddingBottom:8 },
  // ── Comparison navigation ──
  navRow: { display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginBottom:16 },
  navBtn: { background:'#3498db', color:'#fff', border:'none', borderRadius:6, width:36, height:36, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  navLabel: { fontSize:16, fontWeight:600, color:'#2c3e50', minWidth:160, textAlign:'center' },
  // ── Comparison blocks ──
  comparisonRow: { display:'flex', alignItems:'center', justifyContent:'center', gap:40 },
  compCol: { textAlign:'center' },
  compLabel: { fontSize:14, color:'#888', marginBottom:4 },
  compValue: { fontSize:22, fontWeight:700, color:'#2c3e50' },
  compSub: { fontSize:13, color:'#999', marginTop:2 },
  compArrow: { textAlign:'center' },
  expandBtn: { background:'none', border:'1px solid #3498db', color:'#3498db', borderRadius:6, padding:'6px 18px', fontSize:13, fontWeight:600, cursor:'pointer' },
  // ── Table ──
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, color:'#2c3e50', whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  actionItem: { padding:'10px 14px', marginBottom:8, background:'#fafafa', borderRadius:6, display:'flex', alignItems:'center', flexWrap:'wrap' },
  activityItem: { padding:'8px 0', borderBottom:'1px solid #f0f0f0' },
  activityMeta: { marginBottom:2 },
};
